/**
 * Enterprise app-credentials wire contract — the SINGLE authoritative shape of
 * the per-org credential bundle that crosses server → WS push → desktop.
 *
 * Why this lives in @dosia/shared: the same object is produced by the server
 * (apps/web PUT /admin/app-credentials + dosia-org-store) and consumed by the
 * desktop (enterprise-event-client WS parser → enterprise-bundle-runtime apply
 * → materializeFeishuConfig). When each side hand-rolled its own `AppCredentials`
 * interface, a field added/required on one side but missing on the other compiled
 * clean on both and drifted silently — the same failure mode that crashed feishu
 * onboarding (see CLAUDE.md 架构红线 #9, 单一真相源). One type, imported on both
 * sides, makes that drift a build error instead of a runtime crash.
 */
import { normalizeMcpServerNameToBare } from "./mcp-plugin-name.js";
import { DOSIA_PROJECTS_MCP_SERVER_NAME } from "./projects-mcp-contract.js";
import { DOSIA_RELAY_MCP_SERVER_NAME } from "./relay-mcp-contract.js";
/**
 * Whether a string is a valid POSIX environment-variable NAME — the single
 * authoritative rule for {@link AppCredentials.mcpSecrets} keys. Shared across
 * every trust boundary (server intake, WS deserialization, subprocess-env
 * injection) so a name accepted by the producer can never be rejected by a
 * consumer (red line #9, single-source contract). A valid name can't clobber a
 * shell/quoting-sensitive key downstream.
 */
export function isValidEnvVarName(name) {
    return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name);
}
/** The env-var NAME the 腾讯文档 connector's `.mcp.json` references via
 *  `${TENCENT_DOCS_MCP_TOKEN}` for its `Authorization` header. Single source of
 *  truth shared by: the plugin manifest (`plugins/tencent-docs/.mcp.json`), the
 *  personal-credential setter (writes it into {@link AppCredentials.mcpSecrets}),
 *  the connector disconnect (clears it), and the health probe (checks presence).
 *  The header carries the RAW token — no `Bearer` prefix (verified against the
 *  live docs.qq.com/openapi/mcp endpoint). */
export const TENCENT_DOCS_MCP_TOKEN_ENV = "TENCENT_DOCS_MCP_TOKEN";
/**
 * Reserved, ephemeral-only env name used by the enterprise Juguang MCP
 * manifest's `X-Juguang-User-Token` header. It must never be accepted as an
 * org/static credential: desktop and agent-host both remove any persisted or
 * process-provided value before adding a freshly minted per-user token.
 */
export const JUGUANG_USER_TOKEN_ENV = "JUGUANG_USER_TOKEN";
/** Bare `.mcp.json` server key for the enterprise Juguang connector. */
export const JUGUANG_MCP_SERVER_NAME = "catalyst-juguang";
/**
 * Env name carrying the ABSOLUTE PATH of the Codex gateway credential file (never
 * the raw key — `ps eww` would expose an inline key to any local process; a path is
 * safe in argv/env). Wire contract mirrored by
 * `plugins/dosia-codex/skills/codex-gateway-setup/scripts/configure-codex-gateway.py`
 * `resolve_key()`.
 *
 * Lives in @dosia/shared rather than desktop main because it is consumed on BOTH
 * sides of the dependency arrow: desktop writes it, and packages/core's Pi Bash
 * env has to recognize it (see {@link SCRIPT_FACING_MCP_SECRET_ENV_NAMES}).
 */
export const CODEX_GATEWAY_CREDENTIAL_ENV = "DOSIA_CODEX_CREDENTIAL_FILE";
/** Env name carrying the ABSOLUTE PATH of the Codex gateway status file. Injected
 *  whether or not a credential was issued — "why you did NOT get one" is exactly
 *  what a script needs when there is no credential. Mirrored by the same script's
 *  `read_gateway_status()`. */
export const CODEX_GATEWAY_STATUS_ENV = "DOSIA_CODEX_GATEWAY_STATUS_FILE";
/**
 * The **script-facing** members of the `mcpSecrets` channel.
 *
 * That channel carries two structurally different things under one name, and the
 * difference decides which backends can serve them (2026-08-25 类级实证):
 *
 *  (a) **MCP header secrets** — named after a plugin `.mcp.json`'s `${VAR}`
 *      reference ({@link JUGUANG_USER_TOKEN_ENV}, {@link TENCENT_DOCS_MCP_TOKEN_ENV},
 *      `DOSIA_MCP_SECRET`), but on BOTH legs expanded in-process by
 *      cloud-mcp-materializer / `materializePluginMcpServers` directly into
 *      `options.mcpServers[...].headers` before the agent subprocess spawns —
 *      `strictMcpConfig: true` (REQ-91) means the Claude CLI never reads a plugin
 *      `.mcp.json` at load time any more, so there is no `${VAR}`-expansion consumer
 *      left on either leg. **Both backends already work**, and neither needs the
 *      value inside a shell.
 *
 *  (b) **script-facing payloads** — read directly by a script or CLI that the agent
 *      runs through Bash. They ride this channel by convention only. Both backends
 *      now inject ONLY this allowlisted subset into their Bash/subprocess env — the
 *      Claude leg via `pickScriptFacingMcpSecrets` in agent-session.ts (REQ-110,
 *      2026-08-31), the Pi leg the same way in pi-backend.ts (#2661). Before REQ-110
 *      the Claude leg forwarded the WHOLE `mcpSecrets` bundle into the SDK CLI
 *      subprocess env, so every (a)-class header secret leaked into Bash too — a
 *      SIDE EFFECT of `.mcp.json` `${VAR}` expansion that, after REQ-91's
 *      `strictMcpConfig: true`, no longer even happens (the CLI never reads a plugin
 *      `.mcp.json` at load time any more; cloud-mcp-materializer expands headers
 *      in-process instead). Growing this allowlist is how a new script-facing
 *      payload becomes reachable on either leg.
 *
 * Only (b) belongs in a shell env. Merging the whole bundle instead would push
 * `JUGUANG_USER_TOKEN` into Pi's Bash and widen the secret blast radius for zero
 * benefit — that class is reachable through its MCP tools already.
 *
 * Deliberately an ALLOWLIST, not a heuristic (same posture as `SCRIPT_ENV_ALLOWLIST`):
 * an org admin can register arbitrary env names through the cloud MCP-secrets route,
 * and a name of unknown class must default to "MCP header" — the non-leaking side.
 * Grow this list here, in one place, when a new script-facing payload appears.
 */
export const SCRIPT_FACING_MCP_SECRET_ENV_NAMES = [
    CODEX_GATEWAY_CREDENTIAL_ENV,
    CODEX_GATEWAY_STATUS_ENV,
];
/** Narrow an `mcpSecrets` bundle to the script-facing (b) class — what a Bash env
 *  may carry. Empty/absent input → `{}`, so callers can spread unconditionally. */
export function pickScriptFacingMcpSecrets(mcpSecrets) {
    if (!mcpSecrets)
        return {};
    const out = {};
    for (const name of SCRIPT_FACING_MCP_SECRET_ENV_NAMES) {
        const value = mcpSecrets[name];
        if (typeof value === "string" && value.length > 0)
            out[name] = value;
    }
    return out;
}
/**
 * 会话持有的 DOSIA 平台凭证真值——交给 secret-redaction 的 `knownValues`，在工具结果进模型前、
 * 会话记录上传前、写文件门判定时按真值认出（B-203）。
 *
 * = `mcpSecrets` 里**不是** script-facing 的那些值（连接器请求头里的入站密钥、员工身份令牌等）
 *   + 调用方另给的桥票据（`DOSIA_JOB_TOKEN`）。script-facing 那两项是**文件路径**
 *   （Codex 网关凭证文件 / 状态文件的位置，不是凭证本身），按真值替换只会把排查用的路径遮掉。
 * 非字符串与空串丢弃；去重。
 */
export function knownPlatformSecretValues(mcpSecrets, extra = []) {
    const out = new Set();
    if (mcpSecrets) {
        for (const [name, value] of Object.entries(mcpSecrets)) {
            if (SCRIPT_FACING_MCP_SECRET_ENV_NAMES.includes(name))
                continue;
            if (typeof value === "string" && value.length > 0)
                out.add(value);
        }
    }
    for (const value of extra)
        if (typeof value === "string" && value.length > 0)
            out.add(value);
    return [...out];
}
/** Service-auth identity headers shared by the enterprise server and agent-host. */
export const DOSIA_SERVICE_ACTOR_HEADER = "x-dosia-actor-user-id";
export const DOSIA_SERVICE_ORG_HEADER = "x-dosia-org-id";
// ── 会话级连接器身份（短时效 JWT）过期形态 ─────────────────────────────────────
/**
 * 会话级身份 token 的三条腿。共同点：facade 只认一个**短时效**（TTL 10min）JWT，
 * 客户端进程里的 MCP header 在 SDK 子进程建会话那一刻被冻结，所以 token 一过期，
 * 本轮内（或后台子代理里）的调用只会拿到 401 —— 必须重建会话重签，不是让员工
 * 「重新登录」。三个裸名各自的契约常量仍在各自的 contract 文件里（audience 刻意
 * 隔离），这里只把「哪些 server 走会话级身份」收成一份可被门/handler 消费的名单。
 */
export const SESSION_IDENTITY_MCP_SERVER_NAMES = [
    JUGUANG_MCP_SERVER_NAME,
    DOSIA_PROJECTS_MCP_SERVER_NAME,
    DOSIA_RELAY_MCP_SERVER_NAME,
];
/**
 * 「身份 token 过期/无效」的唯一判据。三家 facade 的 401 文案各不相同，之前
 * cron 腿（mcp-bridge）和 marketing-connector 各抄了一份正则，必漂移（红线 #9）。
 *
 * 组成：
 *  - `IDENTITY_TOKEN_EXPIRED` —— 聚光 facade 的机器码。注意 `\b` 在下划线两侧
 *    不成立（`_` 属于 `\w`），所以不能写成 `\btoken expired\b`（同
 *    failure-guidance.ts:35 记的那一课）。
 *  - `身份 JWT` / `JWT 无效或已过期` —— 聚光中文文案。
 *  - `令牌已过期` —— dosia-relay facade（relay-mcp-facade「令牌已过期」分支）。
 *  - `无效或缺失的 x-dosia-(projects|relay)-user-token` —— projects/relay facade 的
 *    缺失/无效分支。header 名恒为小写（Node），这里靠 `i` 兜大小写。
 *  - `token expired` / `401` / `unauthorized` —— 通用兜底（原 cron 正则）。
 */
export const SESSION_IDENTITY_EXPIRED_PATTERN = /IDENTITY_TOKEN_EXPIRED|token[\s_-]*expired|身份\s*JWT|JWT\s*无效或已过期|令牌已过期|无效或缺失的\s*x-dosia-(?:projects|relay)-user-token|\b401\b|unauthorized/i;
/**
 * 中继 / 项目域两家 facade 拒绝员工身份时的 JSON-RPC 错误码（B-204）。
 *
 * facade 在协议内报身份失败（带 id 的请求回 HTTP 200 + 这个错误码，文案仍是上面判据认的那几句），
 * 不回 HTTP 401：Claude 引擎的 MCP 客户端对没配 `Authorization` 头的连接器，收到 401 就走 OAuth
 * 发现与动态注册，文案被换掉，上面的判据看不见。应答形态单源在企业后端
 * `apps/web/server/enterprise/mcp-identity-rejection.ts`。
 *
 * 取值避开 MCP SDK 保留码（-32001 RequestTimeout 会被 pi 引擎判成超时）。客户端认过期一律按文案；
 * 这个码只回答另一件事：facade 在执行任何工具之前就拒了这次请求，写工具也是确定失败、不是「状态未知」。
 */
export const SESSION_IDENTITY_REJECTED_JSONRPC_CODE = -32010;
/** 一段上游错误文本是否属于「会话级身份 token 过期/无效」。 */
export function isSessionIdentityExpiredText(text) {
    if (typeof text !== "string" || text.length === 0)
        return false;
    return SESSION_IDENTITY_EXPIRED_PATTERN.test(text);
}
/**
 * 这个 MCP server 名是否走会话级身份 token。接受裸名，也接受插件命名空间形态
 * （`plugin_<plugin>_<server>` / `plugin:<plugin>:<server>`）—— 模型手上的工具名
 * 两种拼法都可能出现，归一交给 shared 唯一的 normalizer。
 */
export function isSessionIdentityMcpServerName(name) {
    if (typeof name !== "string" || name.length === 0)
        return false;
    const bare = normalizeMcpServerNameToBare(name, SESSION_IDENTITY_MCP_SERVER_NAMES);
    return SESSION_IDENTITY_MCP_SERVER_NAMES.includes(bare);
}
//# sourceMappingURL=enterprise-credentials.js.map